import { useEffect, useRef, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { api } from "../data";
import { NoAccess } from "../components/States";
import { StartLaden } from "../components/Ladeschirm";
import { useAccount, useSwitchStaffOrg, useSwitchTalentOrg } from "./session";
import { t } from "../lib/sprache";

/**
 * Sprungziel der Mails (/go/<art>/<id>?als=team|creator, packages/server/src/outbox.ts): führt nach der Anmeldung zum
 * Objekt im passenden Portal und in der richtigen Agentur (Befund 4). Die Datenbank sagt, zu welcher Agentur das Objekt
 * gehört und ob die Person es als Team oder als Creator sieht (go_target, 0041); der Link nennt das Portal der Mail.
 * Wer beides ist (Gründerin, die auch Creator ist), landet dort, wofür die Mail gedacht war; ohne Angabe im Team.
 */
interface GoTarget {
  org_id: string | null;
  talent_id?: string | null;
  staff: boolean;
  creator: boolean;
}

export function go(kind: string, id: string, creator: boolean, period?: { deal_id: string; month: string } | null): string | null {
  if (kind === "invoice") return creator ? `/creator/rechnungen?rechnung=${id}` : `/agentur/rechnungen/${id}`;
  if (kind === "contract") return creator ? `/creator/vertraege/${id}` : `/agentur/vertraege/${id}`;
  if (kind === "calendar") return creator ? `/creator/kalender?termin=${id}` : `/agentur/kalender?termin=${id}`;
  if (kind === "period") {
    if (!period) return null;
    return creator ? `/creator/rechnungen/einreichen?kooperation=${period.deal_id}&monat=${period.month.slice(0, 7)}`
      : `/agentur/kooperationen/${period.deal_id}?monat=${period.month.slice(0, 7)}`;
  }
  if (kind === "deal") return creator ? `/creator/deals/${id}` : `/agentur/kooperationen/${id}`;
  if (kind === "talent") return creator ? "/creator/profil" : `/agentur/creator/${id}`;
  if (kind === "chat") return creator ? "/creator/nachrichten" : `/agentur/nachrichten/${id}`;
  if (kind === "mail") return creator ? null : `/agentur/postausgang?auftrag=${id}`;
  return null;
}

export function Go() {
  const { kind = "", id = "" } = useParams();
  const [params] = useSearchParams();
  const als = params.get("als");
  const { session, account, loading } = useAccount();
  const switchStaff = useSwitchStaffOrg();
  const switchTalent = useSwitchTalentOrg();
  const [target, setTarget] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // Einmal je Link: der Agenturwechsel lädt das Konto neu, das darf keinen zweiten Durchlauf auslösen.
  // Abgebrochen wird nur, wenn die Seite verschwindet (nicht beim Neuladen des Kontos).
  const started = useRef<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!account) return;
    const key = `${kind}/${id}/${als ?? ""}`;
    if (started.current === key) return;
    started.current = key;
    const cancelled = () => !alive.current;
    void (async () => {
      try {
        const ziel = await api.read<GoTarget | null>("go_target", { p_kind: kind, p_id: id });
        if (!ziel || !ziel.org_id || (!ziel.staff && !ziel.creator)) {
          if (!cancelled()) setFailed(t("Dieser Link führt zu nichts, das für dein Konto freigegeben ist. Vielleicht gehört er zu einer Agentur, in der du nicht (mehr) Mitglied bist.", "This link leads to nothing available to your account. It may belong to an agency you are no longer a member of."));
          return;
        }
        const creator = als === "creator" ? ziel.creator : als === "team" ? !ziel.staff && ziel.creator : !ziel.staff;
        // Arbeitsbereich auf die Agentur des Objekts stellen, bevor die Seite lädt
        if (creator) {
          if (account.talentLinks.some((l) => l.org_id === ziel.org_id)) switchTalent(ziel.org_id);
        } else if (account.memberships.some((m) => m.org_id === ziel.org_id)) {
          switchStaff(ziel.org_id);
        }
        let period: { deal_id: string; month: string } | null = null;
        if (kind === "period") {
          period = (await api.select<{ deal_id: string; month: string }>(creator ? "talent_deal_periods" : "deal_periods", { columns: "deal_id, month", filters: [["id", "eq", id]] }))[0] ?? null;
        }
        const to = go(kind, id, creator, period);
        if (!cancelled()) (to ? setTarget(to) : setFailed(t("Dieser Link führt zu nichts, das für dein Konto freigegeben ist.", "This link leads to nothing available to your account.")));
      } catch {
        if (!cancelled()) setFailed(t("Dieser Link führt zu nichts, das für dein Konto freigegeben ist.", "This link leads to nothing available to your account."));
      }
    })();
    // Wechsel-Funktionen sind je Aufruf neu; maßgeblich sind Konto, Art, Kennung und Portal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, kind, id, als]);

  if (loading) return <StartLaden />;
  if (!session) {
    const back = `/go/${kind}/${id}${als ? `?als=${encodeURIComponent(als)}` : ""}`;
    return <Navigate to={`/anmelden?weiter=${encodeURIComponent(back)}`} replace />;
  }
  if (failed) return <NoAccess text={failed} />;
  if (!target) return <StartLaden />;
  return <Navigate to={target} replace />;
}
